import { cn } from "@/lib/utils";
import type { EstimateChatUIMessage } from "@/hooks/mutation/estimate-chat";
import { getMessageText } from "@/hooks/mutation/estimate-chat";
import { AGENT_TOOL_LABELS, DEFAULT_AGENT_TOOL_LABEL } from "./agentToolLabels.constants";
import { AssistantMarkdown } from "./AssistantMarkdown";
import { toTurnBlocks } from "./streamingAgentTurn.utils";
import { UserMessage } from "./UserMessage";

interface StreamingAgentTurnProps {
  messages: EstimateChatUIMessage[];
  isStreaming: boolean;
}

function ThinkingIndicator() {
  return <p className="text-[11px] text-muted-foreground animate-pulse">Thinking…</p>;
}

/**
 * The turn still streaming, or one that failed. Text renders as it arrives and tool calls render as
 * labels only; proposal cards appear once the saved turn replaces this one.
 */
export function StreamingAgentTurn({ messages, isStreaming }: StreamingAgentTurnProps) {
  // [thinking gap fix] Before the stream's first chunk, the live turn ends with the user message.
  const isAwaitingAssistant = isStreaming && messages[messages.length - 1]?.role === "user";

  return (
    <>
      {messages.map((message) => {
        if (message.role === "user") return <UserMessage key={message.id} content={getMessageText(message)} />;

        const blocks = toTurnBlocks(message.parts);
        // [thinking gap fix] The stream's first chunk creates the assistant message before the model has
        // output anything, and the model thinks silently before its first word. Keep "Thinking…" in
        // its place until something visible arrives. A failed turn with no output shows nothing.
        if (blocks.length === 0) return isStreaming ? <ThinkingIndicator key={message.id} /> : null;

        return (
          <div key={message.id} className="flex max-w-[95%] flex-col gap-2">
            {blocks.map((block, index) =>
              block.kind === "text" ? (
                <AssistantMarkdown key={index}>{block.text}</AssistantMarkdown>
              ) : (
                <p key={index} className={cn("text-[11px] text-muted-foreground", !block.isDone && "animate-pulse")}>
                  {(AGENT_TOOL_LABELS[block.toolName] ?? DEFAULT_AGENT_TOOL_LABEL)(block.successCount)}
                </p>
              ),
            )}
          </div>
        );
      })}
      {isAwaitingAssistant && <ThinkingIndicator />}
    </>
  );
}
